import React from 'react';
import { motion } from 'motion/react';
import { Eye } from 'lucide-react';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';
import type { SlotExperience } from '@/store/slotCanvasStore';
import { SLOT_ANIMATION } from '@/constants/reports';
import { cn } from '@/lib/utils';

export interface SlotCanvasPreviewProps {
  id: string;
  experience: SlotExperience;
  title: string;
  isActive?: boolean;
  onClick: () => void;
}

/**
 * Inline preview card shown in the chat stream when slot rendering is in
 * canvas mode. Clicking the card opens the canvas to this slot.
 *
 * Layout follows the chat mock (Figma 145:22128 "iteration 2, with artefact
 * panel"): an uppercase experience-type label above the title, with an
 * eye + "Viewing"/"Open" affordance on the right.
 */
export function SlotCanvasPreview({
  id,
  experience,
  title,
  isActive,
  onClick,
}: SlotCanvasPreviewProps) {
  const typeLabel = EXPERIENCE_LABELS[experience] ?? experience;

  return (
    <motion.button
      {...SLOT_ANIMATION}
      type="button"
      onClick={onClick}
      data-slot-id={id}
      aria-label={`Open ${title} in canvas`}
      className={cn(
        'group mt-3 w-full flex items-center justify-between gap-3 rounded-xl border border-black/10 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        isActive && 'ring-2 ring-primary/40',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {typeLabel}
        </span>
        <span className="truncate text-sm font-medium text-foreground tracking-[-0.28px]">
          {title}
        </span>
      </span>
      {/* Right-edge affordance — signals the card opens the experience in the
          side canvas; reads "Viewing" while that canvas is open. */}
      <span className="ml-2 flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
        <Eye className="size-3.5" />
        {isActive ? 'Viewing' : 'Open'}
      </span>
    </motion.button>
  );
}
